import React, { useState, useEffect } from 'react';
import { DollarSign, Check, X, Filter, TrendingUp, TrendingDown, Brain, Sparkles, Settings, Plus, Trash2, Edit3, AlertCircle, CheckCircle, Eye } from 'lucide-react';
import api from '../../services/api';

function TransactionCategorization() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [categorizationForm, setCategorizationForm] = useState({
    categorization_type: '',
    expense_category_id: '',
    description: ''
  });
  const [activeTab, setActiveTab] = useState('uncategorized'); // 'uncategorized', 'review', 'categories'
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [trainingStats, setTrainingStats] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    category_name: '',
    category_type: 'EXPENSE',
    is_tax_deductible: true,
    description: ''
  });
  const [reviewTransactions, setReviewTransactions] = useState([]);
  const [aiJobId, setAiJobId] = useState(null);
  const [aiProgress, setAiProgress] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  // Poll for AI job status when job is running
  useEffect(() => {
    if (!aiJobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const res = await api.get(`/ai-categorize/job/${aiJobId}`);
        const job = res.data;

        setAiProgress(job.progress);
        setAiResults({
          total_processed: job.processed,
          auto_categorized: job.auto_categorized,
          needs_review: job.needs_review,
          failed: job.failed,
          status: job.status
        });

        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(pollInterval);
          setAiJobId(null);
          setAiProcessing(false);
          fetchData(); // Refresh data

          if (job.status === 'failed') {
            alert('AI categorization failed: ' + (job.error || 'Unknown error'));
          }
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        clearInterval(pollInterval);
        setAiJobId(null);
        setAiProcessing(false);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [aiJobId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [statsRes, txnsRes, categoriesRes, trainingRes] = await Promise.all([
        api.get('/categorize/stats'),
        api.get('/categorize/uncategorized?limit=50'),
        api.get('/operating/categories'),
        api.get('/categorize/training-stats').catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setTransactions(txnsRes.data || []);
      setCategories(categoriesRes.data || []);
      setTrainingStats(trainingRes.data);

      // Fetch AI-categorized transactions for review (those with AI notes)
      fetchReviewTransactions();
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewTransactions = async () => {
    try {
      // Fetch recently categorized transactions that were AI-categorized
      const res = await api.get('/operating/transactions?limit=50');
      // Filter for AI-categorized ones (those with AI notes)
      const aiCategorized = (res.data || []).filter(txn =>
        txn.notes && txn.notes.includes('AI')
      );
      setReviewTransactions(aiCategorized);
    } catch (err) {
      console.error('Error fetching review transactions:', err);
    }
  };

  const handleAiCategorize = async () => {
    setAiProcessing(true);
    setAiResults(null);
    setAiProgress(0);
    try {
      // Start async AI job - this returns immediately
      // Lower threshold (0.6) means more transactions get auto-categorized
      const res = await api.post('/ai-categorize/batch-categorize-async', null, {
        params: { auto_threshold: 0.6, limit: 50 }
      });
      // Set job ID to start polling
      setAiJobId(res.data.job_id);
    } catch (err) {
      console.error('AI categorization error:', err);
      alert('Failed to start AI categorization: ' + (err.response?.data?.detail || err.message));
      setAiProcessing(false);
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`/operating/categories/${editingCategory.id}`, categoryForm);
      } else {
        await api.post('/operating/categories', categoryForm);
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({
        category_name: '',
        category_type: 'EXPENSE',
        is_tax_deductible: true,
        description: ''
      });
      fetchData();
    } catch (err) {
      alert('Failed to save category: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/operating/categories/${categoryId}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete category: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRecategorize = async (txnId, newCategory, newCategoryId) => {
    try {
      await api.post('/categorize/recategorize', {
        bank_transaction_id: txnId,
        new_category: newCategory,
        new_category_id: newCategoryId,
        notes: 'Manual correction from review'
      });
      fetchData();
    } catch (err) {
      alert('Failed to recategorize: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCategorize = async () => {
    if (!selectedTxn || !categorizationForm.categorization_type) {
      alert('Please select a categorization type');
      return;
    }

    try {
      await api.post('/categorize/categorize', {
        bank_transaction_id: selectedTxn.id,
        categorization_type: categorizationForm.categorization_type,
        expense_category_id: categorizationForm.expense_category_id || null,
        description: categorizationForm.description || null
      });

      // Close modal and refresh
      setSelectedTxn(null);
      setCategorizationForm({
        categorization_type: '',
        expense_category_id: '',
        description: ''
      });
      fetchData();
    } catch (err) {
      console.error('Error categorizing transaction:', err);
      alert('Failed to categorize: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount) || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isIncome = (amount) => amount < 0; // Negative = deposit = income

  const expenseCategories = categories.filter(c => c.category_type === 'EXPENSE');
  const revenueCategories = categories.filter(c => c.category_type === 'REVENUE');

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Transaction Categorization</h2>
          <p className="text-cyan-300 mt-1">Review and categorize bank transactions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleAiCategorize}
            disabled={aiProcessing || transactions.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition min-w-[240px]"
          >
            {aiProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Processing... {aiProgress}%
              </>
            ) : (
              <>
                <Brain size={18} />
                Auto-Categorize with Astraea
              </>
            )}
          </button>
        </div>
      </div>

      {/* AI Results Banner */}
      {aiResults && (
        <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Sparkles className="text-cyan-400" size={24} />
            <div className="flex-1">
              <h3 className="text-white font-semibold">
                {aiResults.status === 'running' ? 'Astraea Categorization In Progress...' : 'Astraea Categorization Complete'}
              </h3>
              <p className="text-cyan-300 text-sm">
                {aiResults.status === 'running' ? `Processing transaction ${aiResults.total_processed}...` : `Processed ${aiResults.total_processed} transactions:`}
                <span className="text-green-400 ml-2">{aiResults.auto_categorized} auto-categorized</span>,
                <span className="text-yellow-400 ml-2">{aiResults.needs_review} need review</span>,
                <span className="text-red-400 ml-2">{aiResults.failed} failed</span>
              </p>
              {aiResults.status === 'running' && (
                <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${aiProgress}%` }}
                  />
                </div>
              )}
            </div>
            {aiResults.status !== 'running' && (
              <button onClick={() => setAiResults(null)} className="ml-auto text-cyan-400 hover:text-white">
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Training Stats */}
      {trainingStats && trainingStats.total_corrections > 0 && (
        <div className="bg-slate-800/30 border border-cyan-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-cyan-300 text-sm">
            <Brain size={16} />
            <span>Astraea has learned from <span className="text-white font-semibold">{trainingStats.total_corrections}</span> corrections</span>
            {trainingStats.recent_corrections_30d > 0 && (
              <span className="text-cyan-400">({trainingStats.recent_corrections_30d} in last 30 days)</span>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-cyan-500">
          <h3 className="text-sm font-medium text-cyan-300 mb-2">Total Transactions</h3>
          <p className="text-3xl font-bold text-white">{stats?.total || 0}</p>
        </div>

        <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-orange-500">
          <h3 className="text-sm font-medium text-orange-400 mb-2">Uncategorized</h3>
          <p className="text-3xl font-bold text-orange-400">{stats?.uncategorized || 0}</p>
        </div>

        <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-green-500">
          <h3 className="text-sm font-medium text-green-400 mb-2">Categorized</h3>
          <p className="text-3xl font-bold text-green-400">{stats?.categorized || 0}</p>
        </div>

        <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-blue-500">
          <h3 className="text-sm font-medium text-blue-400 mb-2">Categories</h3>
          <p className="text-3xl font-bold text-blue-400">{categories.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-cyan-500/20 pb-2">
        <button
          onClick={() => setActiveTab('uncategorized')}
          className={`px-4 py-2 rounded-t-lg font-medium transition ${
            activeTab === 'uncategorized'
              ? 'bg-cyan-500/20 text-white border-b-2 border-cyan-500'
              : 'text-cyan-300 hover:text-white hover:bg-slate-800/50'
          }`}
        >
          <Filter size={16} className="inline mr-2" />
          Uncategorized ({transactions.length})
        </button>
        <button
          onClick={() => setActiveTab('review')}
          className={`px-4 py-2 rounded-t-lg font-medium transition ${
            activeTab === 'review'
              ? 'bg-cyan-500/20 text-white border-b-2 border-cyan-500'
              : 'text-cyan-300 hover:text-white hover:bg-slate-800/50'
          }`}
        >
          <Eye size={16} className="inline mr-2" />
          Review AI Work ({reviewTransactions.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2 rounded-t-lg font-medium transition ${
            activeTab === 'categories'
              ? 'bg-cyan-500/20 text-white border-b-2 border-cyan-500'
              : 'text-cyan-300 hover:text-white hover:bg-slate-800/50'
          }`}
        >
          <Settings size={16} className="inline mr-2" />
          Manage Categories
        </button>
      </div>

      {/* Uncategorized Transactions Tab */}
      {activeTab === 'uncategorized' && (
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="px-6 py-4 border-b border-cyan-500/20">
            <h3 className="text-lg font-semibold text-white">
              Uncategorized Transactions (Showing 50 most recent)
            </h3>
          </div>

          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Description</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Merchant</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Account</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Amount</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Type</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="border-t border-cyan-500/10 hover:bg-slate-700/50">
                      <td className="py-3 px-4 text-sm text-white">{formatDate(txn.date)}</td>
                      <td className="py-3 px-4 text-sm max-w-md text-white">
                        <div className="truncate">{txn.name}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300">{txn.merchant_name || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-400">{txn.bank_connection_name}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isIncome(txn.amount) ? (
                            <TrendingDown className="text-green-400" size={16} />
                          ) : (
                            <TrendingUp className="text-red-400" size={16} />
                          )}
                          <span className={`font-medium ${
                            isIncome(txn.amount) ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {formatCurrency(txn.amount)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          isIncome(txn.amount)
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {isIncome(txn.amount) ? 'Income' : 'Expense'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedTxn(txn);
                            setCategorizationForm({
                              categorization_type: isIncome(txn.amount) ? 'OPERATING_INCOME' : 'OPERATING_EXPENSE',
                              expense_category_id: '',
                              description: txn.name
                            });
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm rounded hover:from-cyan-600 hover:to-blue-600 transition"
                        >
                          <Filter size={14} />
                          Categorize
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Check className="mx-auto text-green-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-white mb-2">All Caught Up!</h3>
              <p className="text-cyan-300">All bank transactions have been categorized.</p>
            </div>
          )}
        </div>
      )}

      {/* Review AI Work Tab */}
      {activeTab === 'review' && (
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="px-6 py-4 border-b border-cyan-500/20">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Brain size={20} className="text-cyan-400" />
              Review Astraea's Categorizations
            </h3>
            <p className="text-cyan-300 text-sm mt-1">
              Review and correct AI-categorized transactions. Corrections train Astraea to be more accurate.
            </p>
          </div>

          {reviewTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-800">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">AI Reasoning</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewTransactions.map((txn) => (
                    <tr key={txn.id} className="border-t border-cyan-500/10 hover:bg-slate-700/50">
                      <td className="py-3 px-4 text-sm text-white">{formatDate(txn.transaction_date)}</td>
                      <td className="py-3 px-4 text-sm max-w-xs text-white">
                        <div className="truncate">{txn.description}</div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${
                          txn.transaction_type === 'INCOME' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {formatCurrency(txn.amount)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-cyan-300">{txn.category_name || 'Uncategorized'}</td>
                      <td className="py-3 px-4 text-sm text-slate-400 max-w-xs">
                        <div className="truncate">{txn.notes}</div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {/* Mark as correct - no action needed */}}
                            className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition"
                            title="Correct"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTxn({ ...txn, id: txn.bank_transaction_id });
                              setCategorizationForm({
                                categorization_type: txn.transaction_type === 'INCOME' ? 'OPERATING_INCOME' : 'OPERATING_EXPENSE',
                                expense_category_id: txn.category_id || '',
                                description: txn.description
                              });
                            }}
                            className="p-1.5 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 transition"
                            title="Correct this"
                          >
                            <Edit3 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Brain className="mx-auto text-cyan-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-white mb-2">No AI Categorizations to Review</h3>
              <p className="text-cyan-300">Click "Auto-Categorize with Astraea" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Manage Categories Tab */}
      {activeTab === 'categories' && (
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="px-6 py-4 border-b border-cyan-500/20 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-white">Expense & Revenue Categories</h3>
              <p className="text-cyan-300 text-sm mt-1">Add, edit, or remove categories for transaction classification</p>
            </div>
            <button
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm({
                  category_name: '',
                  category_type: 'EXPENSE',
                  is_tax_deductible: true,
                  description: ''
                });
                setShowCategoryModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition"
            >
              <Plus size={18} />
              Add Category
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Expense Categories */}
              <div>
                <h4 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                  <TrendingUp size={16} />
                  Expense Categories
                </h4>
                <div className="space-y-2">
                  {categories.filter(c => c.category_type === 'EXPENSE').map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-cyan-500/10">
                      <div>
                        <span className="text-white font-medium">{cat.category_name}</span>
                        {cat.is_tax_deductible && (
                          <span className="ml-2 text-xs text-green-400">(Tax Deductible)</span>
                        )}
                        {cat.description && (
                          <p className="text-xs text-slate-400 mt-1">{cat.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingCategory(cat);
                            setCategoryForm({
                              category_name: cat.category_name,
                              category_type: cat.category_type,
                              is_tax_deductible: cat.is_tax_deductible,
                              description: cat.description || ''
                            });
                            setShowCategoryModal(true);
                          }}
                          className="p-1.5 text-cyan-400 hover:bg-cyan-500/20 rounded transition"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Revenue Categories */}
              <div>
                <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                  <TrendingDown size={16} />
                  Revenue Categories
                </h4>
                <div className="space-y-2">
                  {categories.filter(c => c.category_type === 'REVENUE').map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-cyan-500/10">
                      <div>
                        <span className="text-white font-medium">{cat.category_name}</span>
                        {cat.description && (
                          <p className="text-xs text-slate-400 mt-1">{cat.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingCategory(cat);
                            setCategoryForm({
                              category_name: cat.category_name,
                              category_type: cat.category_type,
                              is_tax_deductible: cat.is_tax_deductible,
                              description: cat.description || ''
                            });
                            setShowCategoryModal(true);
                          }}
                          className="p-1.5 text-cyan-400 hover:bg-cyan-500/20 rounded transition"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Add/Edit Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-md w-full border border-cyan-500/30">
            <div className="border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h2>
              <button onClick={() => setShowCategoryModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">Category Name *</label>
                <input
                  type="text"
                  value={categoryForm.category_name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, category_name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g., Court Filing Fees"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">Type *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCategoryForm({ ...categoryForm, category_type: 'EXPENSE' })}
                    className={`py-2 px-4 rounded-lg font-medium transition ${
                      categoryForm.category_type === 'EXPENSE'
                        ? 'bg-red-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategoryForm({ ...categoryForm, category_type: 'REVENUE' })}
                    className={`py-2 px-4 rounded-lg font-medium transition ${
                      categoryForm.category_type === 'REVENUE'
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Revenue
                  </button>
                </div>
              </div>

              {categoryForm.category_type === 'EXPENSE' && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="taxDeductible"
                    checked={categoryForm.is_tax_deductible}
                    onChange={(e) => setCategoryForm({ ...categoryForm, is_tax_deductible: e.target.checked })}
                    className="w-4 h-4 text-cyan-500 bg-slate-800 border-cyan-500/30 rounded focus:ring-cyan-500"
                  />
                  <label htmlFor="taxDeductible" className="text-sm text-cyan-300">Tax Deductible</label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">Description (Optional)</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                  rows={2}
                  placeholder="Brief description of this category"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 border border-cyan-500/30 text-slate-300 rounded-lg hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategory}
                  disabled={!categoryForm.category_name}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50"
                >
                  {editingCategory ? 'Save Changes' : 'Add Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categorization Modal */}
      {selectedTxn && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-cyan-500/30">
            <div className="sticky top-0 bg-slate-900 border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Categorize Transaction</h2>
              <button onClick={() => setSelectedTxn(null)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Transaction Details */}
              <div className="bg-slate-800/50 p-4 rounded-lg border border-cyan-500/20">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Date</p>
                    <p className="font-medium text-white">{formatDate(selectedTxn.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Amount</p>
                    <p className={`font-bold text-lg ${
                      isIncome(selectedTxn.amount) ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {isIncome(selectedTxn.amount) ? '+' : '-'}{formatCurrency(selectedTxn.amount)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-400">Description</p>
                    <p className="font-medium text-white">{selectedTxn.name}</p>
                  </div>
                  {selectedTxn.merchant_name && (
                    <div className="col-span-2">
                      <p className="text-sm text-slate-400">Merchant</p>
                      <p className="font-medium text-white">{selectedTxn.merchant_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Categorization Type */}
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Categorization Type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCategorizationForm({ ...categorizationForm, categorization_type: 'OPERATING_INCOME', expense_category_id: '' })}
                    className={`py-3 px-4 rounded-lg font-medium transition ${
                      categorizationForm.categorization_type === 'OPERATING_INCOME'
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Operating Income
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategorizationForm({ ...categorizationForm, categorization_type: 'OPERATING_EXPENSE', expense_category_id: '' })}
                    className={`py-3 px-4 rounded-lg font-medium transition ${
                      categorizationForm.categorization_type === 'OPERATING_EXPENSE'
                        ? 'bg-red-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Operating Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategorizationForm({ ...categorizationForm, categorization_type: 'IGNORED', expense_category_id: '' })}
                    className={`py-3 px-4 rounded-lg font-medium transition ${
                      categorizationForm.categorization_type === 'IGNORED'
                        ? 'bg-slate-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Ignore (Personal)
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategorizationForm({ ...categorizationForm, categorization_type: 'TRANSFER', expense_category_id: '' })}
                    className={`py-3 px-4 rounded-lg font-medium transition ${
                      categorizationForm.categorization_type === 'TRANSFER'
                        ? 'bg-cyan-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    Transfer
                  </button>
                </div>
              </div>

              {/* Category (for income/expense) */}
              {(categorizationForm.categorization_type === 'OPERATING_INCOME' ||
                categorizationForm.categorization_type === 'OPERATING_EXPENSE') && (
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">
                    Category
                  </label>
                  <select
                    value={categorizationForm.expense_category_id}
                    onChange={(e) => setCategorizationForm({ ...categorizationForm, expense_category_id: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white"
                  >
                    <option value="">None</option>
                    {(categorizationForm.categorization_type === 'OPERATING_INCOME' ? revenueCategories : expenseCategories).map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description Override */}
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  Description (Optional Override)
                </label>
                <input
                  type="text"
                  value={categorizationForm.description}
                  onChange={(e) => setCategorizationForm({ ...categorizationForm, description: e.target.value })}
                  placeholder="Leave blank to use transaction name"
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-slate-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setSelectedTxn(null)}
                  className="px-6 py-2 border border-cyan-500/30 text-slate-300 rounded-lg hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCategorize}
                  className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600"
                >
                  Categorize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionCategorization;
